import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  ArrowLeft,
  FolderOpen,
  Target,
  FileText,
  Clock,
  Plus,
  User,
  Pencil,
  Save,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { GoalForm } from "./GoalForm";
import { CaseTimeline, buildTimelineEvents } from "./CaseTimeline";
import type { Goal } from "./CasesList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CaseDetailData {
  id: number;
  person_id: number;
  agency_id: number;
  category_id: number | null;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string | null;
  created_by: number | null;
  created_at: string;
  category_name?: string;
  author_first_name?: string;
  author_last_name?: string;
  client_first_name?: string;
  client_last_name?: string;
  goals: Goal[];
  notes: Array<{
    id: number;
    note_date: string;
    note_text: string;
    note_type?: string;
    author_first_name?: string;
    author_last_name?: string;
  }>;
}

interface CaseDetailProps {
  caseId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-800",
  in_progress: "bg-blue-100 text-blue-800",
  closed: "bg-gray-100 text-gray-800",
  on_hold: "bg-amber-100 text-amber-800",
};

const goalStatusColors: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  achieved: "bg-green-100 text-green-700",
  not_achieved: "bg-red-100 text-red-700",
  deferred: "bg-amber-100 text-amber-700",
};

const caseStatusOptions: SelectOption[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
  { value: "on_hold", label: "On Hold" },
];

const noteTypeOptions: SelectOption[] = [
  { value: "general", label: "General" },
  { value: "case_management", label: "Case Management" },
  { value: "clinical", label: "Clinical" },
  { value: "legal", label: "Legal" },
  { value: "counseling", label: "Counseling" },
  { value: "advocacy", label: "Advocacy" },
  { value: "other", label: "Other" },
];

const detailTabs = [
  { id: "overview", label: "Overview", icon: FolderOpen },
  { id: "goals", label: "Goals", icon: Target },
  { id: "notes", label: "Notes", icon: FileText },
  { id: "timeline", label: "Timeline", icon: Clock },
] as const;

type TabId = (typeof detailTabs)[number]["id"];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CaseDetail({ caseId }: CaseDetailProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [goalFormTarget, setGoalFormTarget] = useState<{
    goal?: Goal;
  } | null>(null);
  const [isEditingCase, setIsEditingCase] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);

  const { data: caseData, isLoading } = useQuery<{ data: CaseDetailData }>({
    queryKey: ["case-detail", caseId],
    queryFn: () => api.get<{ data: CaseDetailData }>(`/cases/${caseId}`),
    enabled: !!caseId,
  });

  const caseRecord = caseData?.data;

  const invalidateCase = () => {
    queryClient.invalidateQueries({ queryKey: ["case-detail", caseId] });
    queryClient.invalidateQueries({ queryKey: ["all-cases"] });
    queryClient.invalidateQueries({ queryKey: ["case-stats"] });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/cases">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        </div>
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      </div>
    );
  }

  if (!caseRecord) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/cases">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Case not found</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          The case record could not be loaded.
        </p>
      </div>
    );
  }

  const clientName =
    caseRecord.client_first_name && caseRecord.client_last_name
      ? `${caseRecord.client_first_name} ${caseRecord.client_last_name}`
      : `Client #${caseRecord.person_id}`;

  const authorName =
    caseRecord.author_first_name && caseRecord.author_last_name
      ? `${caseRecord.author_first_name} ${caseRecord.author_last_name}`
      : null;

  const timelineEvents = buildTimelineEvents({
    created_at: caseRecord.created_at,
    status: caseRecord.status,
    description: caseRecord.description,
    goals: caseRecord.goals,
    notes: caseRecord.notes,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/cases">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">
                {caseRecord.category_name ?? "General Case"} #{caseRecord.id}
              </h1>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                  statusColors[caseRecord.status ?? "open"] ??
                    statusColors.open,
                )}
              >
                {(caseRecord.status ?? "open").replace("_", " ")}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              <Link
                to={`/clients/${caseRecord.person_id}`}
                className="text-primary hover:underline"
              >
                {clientName}
              </Link>
              {authorName && (
                <span>
                  {" "}
                  &middot; Staff: {authorName}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setIsEditingCase(true);
              setActiveTab("overview");
            }}
          >
            <Pencil className="h-4 w-4" />
            Edit Case
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setShowAddNote(true);
              setActiveTab("notes");
            }}
          >
            <FileText className="h-4 w-4" />
            Add Note
          </Button>
          <Button
            className="gap-2"
            onClick={() => {
              setGoalFormTarget({});
              setActiveTab("goals");
            }}
          >
            <Plus className="h-4 w-4" />
            Add Goal
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {detailTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Goal form */}
      {goalFormTarget && (
        <GoalForm
          personId={String(caseRecord.person_id)}
          caseId={caseRecord.id}
          goal={goalFormTarget.goal}
          onClose={() => {
            setGoalFormTarget(null);
            invalidateCase();
          }}
        />
      )}

      {/* Tab content */}
      {activeTab === "overview" && (
        isEditingCase ? (
          <EditCaseForm
            caseRecord={caseRecord}
            onSaved={() => {
              setIsEditingCase(false);
              invalidateCase();
            }}
            onCancel={() => setIsEditingCase(false)}
          />
        ) : (
          <OverviewTab caseRecord={caseRecord} clientName={clientName} authorName={authorName} />
        )
      )}
      {activeTab === "goals" && (
        <GoalsTab
          goals={caseRecord.goals}
          onAddGoal={() => setGoalFormTarget({})}
          onEditGoal={(goal) => setGoalFormTarget({ goal })}
        />
      )}
      {activeTab === "notes" && (
        <NotesTab
          notes={caseRecord.notes}
          personId={String(caseRecord.person_id)}
          showAddNote={showAddNote}
          onNoteAdded={() => {
            setShowAddNote(false);
            invalidateCase();
          }}
          onCancelAdd={() => setShowAddNote(false)}
        />
      )}
      {activeTab === "timeline" && (
        <CaseTimeline events={timelineEvents} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Case Form
// ---------------------------------------------------------------------------

function EditCaseForm({
  caseRecord,
  onSaved,
  onCancel,
}: {
  caseRecord: CaseDetailData;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState(caseRecord.description ?? "");
  const [status, setStatus] = useState(caseRecord.status ?? "open");
  const [startDate, setStartDate] = useState(caseRecord.start_date ?? "");
  const [endDate, setEndDate] = useState(caseRecord.end_date ?? "");
  const [error, setError] = useState("");

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.put(`/clients/${caseRecord.person_id}/cases/${caseRecord.id}`, data),
    onSuccess: () => onSaved(),
    onError: (err) => setError((err as Error)?.message ?? "Failed to save"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate({
      description,
      status,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Edit Case Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Case description..."
                className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Select
                label="Status"
                options={caseStatusOptions}
                value={status}
                onChange={setStatus}
              />
              <Input
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Button type="submit" className="gap-2" loading={updateMutation.isPending}>
          <Save className="h-4 w-4" />
          Save Changes
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({
  caseRecord,
  clientName,
  authorName,
}: {
  caseRecord: CaseDetailData;
  clientName: string;
  authorName: string | null;
}) {
  const goalsInProgress = caseRecord.goals.filter(
    (g) => g.status === "in_progress",
  ).length;
  const goalsCompleted = caseRecord.goals.filter(
    (g) => g.status === "achieved",
  ).length;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Case Information</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-3 text-sm">
            <DetailRow label="Case ID" value={`#${caseRecord.id}`} />
            <DetailRow
              label="Category"
              value={caseRecord.category_name ?? "General"}
            />
            <DetailRow
              label="Status"
              value={(caseRecord.status ?? "open").replace("_", " ")}
            />
            <DetailRow
              label="Start Date"
              value={
                caseRecord.start_date
                  ? new Date(caseRecord.start_date).toLocaleDateString()
                  : "Not set"
              }
            />
            <DetailRow
              label="End Date"
              value={
                caseRecord.end_date
                  ? new Date(caseRecord.end_date).toLocaleDateString()
                  : "Not set"
              }
            />
            <DetailRow
              label="Created"
              value={new Date(caseRecord.created_at).toLocaleDateString()}
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">People</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <DetailRow label="Client" value={clientName} />
            </div>
            {authorName && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <DetailRow label="Assigned Staff" value={authorName} />
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {caseRecord.description && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {caseRecord.description}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Goals Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-foreground">
                {caseRecord.goals.length}
              </p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {goalsInProgress}
              </p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {goalsCompleted}
              </p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Notes</CardTitle>
        </CardHeader>
        <CardContent>
          {caseRecord.notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notes yet.</p>
          ) : (
            <div className="space-y-2">
              {caseRecord.notes.slice(0, 3).map((note) => (
                <div key={note.id} className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">
                    {new Date(note.note_date).toLocaleDateString()}
                    {note.author_first_name &&
                      ` - ${note.author_first_name} ${note.author_last_name}`}
                  </p>
                  <p className="mt-0.5 text-sm text-foreground line-clamp-2">
                    {note.note_text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goals Tab
// ---------------------------------------------------------------------------

function GoalsTab({
  goals,
  onAddGoal,
  onEditGoal,
}: {
  goals: Goal[];
  onAddGoal: () => void;
  onEditGoal: (goal: Goal) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Goals ({goals.length})</h2>
        <Button size="sm" className="gap-2" onClick={onAddGoal}>
          <Plus className="h-4 w-4" />
          Add Goal
        </Button>
      </div>

      {goals.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Target className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No goals set for this case.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {goals.map((goal) => (
            <Card
              key={goal.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onEditGoal(goal)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{goal.goal_text}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {goal.target_date && (
                        <span>
                          Target:{" "}
                          {new Date(goal.target_date).toLocaleDateString()}
                        </span>
                      )}
                      {goal.completed_date && (
                        <span>
                          Completed:{" "}
                          {new Date(goal.completed_date).toLocaleDateString()}
                        </span>
                      )}
                      {goal.outcome && <span>Outcome: {goal.outcome}</span>}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                      goalStatusColors[goal.status ?? "pending"] ??
                        goalStatusColors.pending,
                    )}
                  >
                    {(goal.status ?? "pending").replace("_", " ")}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes Tab with Add Note form
// ---------------------------------------------------------------------------

function NotesTab({
  notes,
  personId,
  showAddNote,
  onNoteAdded,
  onCancelAdd,
}: {
  notes: CaseDetailData["notes"];
  personId: string;
  showAddNote: boolean;
  onNoteAdded: () => void;
  onCancelAdd: () => void;
}) {
  const [isAdding, setIsAdding] = useState(showAddNote);

  // Sync with parent prop
  if (showAddNote && !isAdding) {
    setIsAdding(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Notes ({notes.length})</h2>
        {!isAdding && (
          <Button size="sm" className="gap-2" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4" />
            Add Note
          </Button>
        )}
      </div>

      {isAdding && (
        <AddNoteForm
          personId={personId}
          onSaved={() => {
            setIsAdding(false);
            onNoteAdded();
          }}
          onCancel={() => {
            setIsAdding(false);
            onCancelAdd();
          }}
        />
      )}

      {notes.length === 0 && !isAdding ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No notes linked to this case.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    {note.author_first_name
                      ? `${note.author_first_name} ${note.author_last_name}`
                      : "Staff"}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {note.note_type && (
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
                        {note.note_type.replace("_", " ")}
                      </span>
                    )}
                    <CardDescription>
                      {new Date(note.note_date).toLocaleDateString()}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {note.note_text}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Note Form (inline within case detail)
// ---------------------------------------------------------------------------

function AddNoteForm({
  personId,
  onSaved,
  onCancel,
}: {
  personId: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("case_management");
  const [noteDate, setNoteDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post(`/clients/${personId}/notes`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", personId] });
      onSaved();
    },
    onError: (err) => setError((err as Error)?.message ?? "Failed to save note"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!noteText.trim()) return;
    createMutation.mutate({
      title: title || undefined,
      noteText,
      noteType,
      noteDate,
      noteTime: new Date().toTimeString().split(" ")[0].slice(0, 5),
    });
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">New Note</CardTitle>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Note title..."
          />
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Note Text <span className="text-destructive">*</span>
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              placeholder="Enter your note..."
              required
              className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Note Type"
              options={noteTypeOptions}
              value={noteType}
              onChange={setNoteType}
            />
            <Input
              label="Date"
              type="date"
              value={noteDate}
              onChange={(e) => setNoteDate(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              size="sm"
              className="gap-2"
              disabled={!noteText.trim()}
              loading={createMutation.isPending}
            >
              <Save className="h-4 w-4" />
              Save Note
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium capitalize">{value}</dd>
    </div>
  );
}
